import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FormProvider, Validate, useForm } from "react-hook-form";

import { TextWithLoader, Button, HookFormInputGroup } from "components";
import { useUpdateCreditReviewInformationMutation } from "queries";

import { ReactComponent as CancelIcon } from "static/images/cancel.svg";
import { ReactComponent as CheckDone } from "static/images/checked.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./../autoCheckList.module.scss";

type MerchantFieldFormProps = {
	isEditable?: boolean;
	label: string;
	name: string;
	normalizeOnBlur?: Function;
	normalize?: Function;
	validate?: Validate<any, any> | Record<string, Validate<any, any>>;
	creditReviewData: { [key: string]: any };
	setCreditReviewData: Function;
	applicationId: string | number;
	normalizer?: Function[];
};

const MerchantFieldForm = ({
	isEditable,
	label,
	name,
	normalize,
	validate,
	normalizeOnBlur,
	creditReviewData,
	setCreditReviewData,
	applicationId,
	normalizer,
}: MerchantFieldFormProps) => {
	const [showEditField, setShowEditField] = useState(false);

	const normalizedCreditReviewField = useMemo(
		() =>
			normalizer?.reduce(
				(val, normalizer) => normalizer(val) || "",
				creditReviewData[name],
			),
		[creditReviewData, name, normalizer],
	);

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		shouldUnregister: true,
		defaultValues: {
			[name]: normalizedCreditReviewField || "",
		},
	});

	const {
		handleSubmit,
		register,
		formState: { errors, isValid },
		reset,
		watch,
	} = formMethods;

	useEffect(() => {
		reset({ [name]: normalizedCreditReviewField });
	}, [name, normalizedCreditReviewField, reset]);

	const handleCancelEdit = () => setShowEditField(false);

	const isCreditReviewExist = !!watch(name);

	const {
		mutate: updateCreditReviewData,
		isLoading: isLoadingUpdateCreditReviewData,
	} = useUpdateCreditReviewInformationMutation({
		applicationId,
		creditReviewData,
		setCreditReviewData,
		setShowEditField,
	});

	const onSubmit = useCallback(
		formValues => updateCreditReviewData(formValues),
		[updateCreditReviewData],
	);

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={handleSubmit(onSubmit)} className="d-flex">
				{showEditField ? (
					<div className="d-flex w-100">
						<HookFormInputGroup
							name={name}
							register={register}
							validator={validate}
							emptyLabel={false}
							placeholder="0.00"
							colInput={6}
							colLabel={6}
							errors={errors}
							label={label}
							blurLoading={isLoadingUpdateCreditReviewData}
							normalize={normalize}
							normalizeOnBlur={normalizeOnBlur}
							className={`w-100 ${styles.mb0}`}
						/>
						<div className="d-flex">
							<Button
								onClick={handleCancelEdit}
								icon={<CancelIcon />}
								btnIcon
								variant="secondary-danger"
								size="sm"
								disabled={isLoadingUpdateCreditReviewData}
							/>
							<Button
								type="submit"
								icon={<CheckDone />}
								btnIcon
								variant="secondary"
								size="sm"
								disabled={
									!isCreditReviewExist ||
									!isValid ||
									isLoadingUpdateCreditReviewData
								}
							/>
						</div>
					</div>
				) : (
					<div className={`${styles.merchantData} d-flex w-100`}>
						<TextWithLoader
							label={label}
							value={normalizedCreditReviewField}
							loading={isLoadingUpdateCreditReviewData}
							labelCol={6}
							valueCol={6}
							className="w-100 mb-0 align-items-center"
							valueClass={`
								text-right pl-2 pr-0 ${styles.whiteSpaceNowrap}`}
						/>
						{isEditable && (
							<Button
								onClick={() => setShowEditField(true)}
								icon={<EditIcon />}
								btnIcon
								className="ml-4"
								variant="secondary"
								size="sm"
							/>
						)}
					</div>
				)}
			</form>
		</FormProvider>
	);
};

export default MerchantFieldForm;
